import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Phone } from 'lucide-react';
import SpiritualRobot from './SpiritualRobot';
import { trackConsultationCTAClick } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;
const CALL_PHONE = '+919311369729';

function getSessionId() {
  let sid = localStorage.getItem('chatbot_sid');
  if (!sid) {
    sid = 'cb-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    localStorage.setItem('chatbot_sid', sid);
  }
  return sid;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      <div className="flex gap-1">
        <span className="w-2 h-2 bg-yellow-500/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-yellow-500/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-yellow-500/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span className="text-xs text-yellow-600/70 ml-2">typing...</span>
    </div>
  );
}

function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`} data-testid={`chat-msg-${msg.role}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        isUser
          ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-white rounded-br-md'
          : 'bg-[#3d101e] text-gray-200 border border-yellow-900/30 rounded-bl-md'
      }`}>
        {msg.text}
      </div>
    </div>
  );
}

/**
 * FloatingActions — single launcher replacing 3 separate floating widgets.
 * Tap robot → speed-dial menu (Chat / WhatsApp / Call) → either opens chat window
 * or fires WhatsApp/call link.
 */
export default function FloatingActions() {
  const { pathname } = useLocation();

  // Hide on pages that already have prominent inline CTAs / dense content
  // so floating buttons don't cover important text (Pucho Guruji answer,
  // T&C disclaimer, payment form, kundli birth-details form etc).
  const HIDE_ON = ['/poocho-guruji', '/kundli', '/gun-milan', '/admin'];
  const hide = HIDE_ON.some((p) => pathname === p || pathname.startsWith(p + '/'));

  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [visitorName, setVisitorName] = useState('');
  const [visitorPhone, setVisitorPhone] = useState('');
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);
  useEffect(() => { if (chatOpen && inputRef.current) inputRef.current.focus(); }, [chatOpen]);

  // Allow other components (e.g. Ecosystem section card) to open the chat
  useEffect(() => {
    const handler = () => {
      setMenuOpen(false);
      setChatOpen(true);
      setMessages(prev => prev.length === 0 ? [{
        role: 'assistant',
        text: 'नमस्कार! ViratAstro में आपका स्वागत है। मैं आपकी कैसे सहायता कर सकता हूँ? ज्योतिष, वास्तु, कुंडली या किसी भी सेवा के बारे में पूछिए।'
      }] : prev);
    };
    window.addEventListener('viratastro:open-chat', handler);
    return () => window.removeEventListener('viratastro:open-chat', handler);
  }, []);

  const openChat = useCallback(() => {
    setMenuOpen(false);
    setChatOpen(true);
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        text: 'नमस्कार! ViratAstro में आपका स्वागत है। मैं आपकी कैसे सहायता कर सकता हूँ? ज्योतिष, वास्तु, कुंडली या किसी भी सेवा के बारे में पूछिए।'
      }]);
    }
  }, [messages.length]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages(prev => [...prev, { role: 'user', text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/chatbot/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId.current, message: text,
          visitor_name: visitorName || null, visitor_phone: visitorPhone || null,
        }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', text: data.reply }]);
      const userMsgCount = messages.filter(m => m.role === 'user').length + 1;
      if (userMsgCount >= 3 && !nameSubmitted && !showNamePrompt) setShowNamePrompt(true);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'कुछ technical issue आ रहा है। कृपया WhatsApp पर संपर्क करें: +91-9311369729' }]);
    } finally { setLoading(false); }
  };

  const handleNameSubmit = () => {
    if (visitorName.trim()) {
      setNameSubmitted(true);
      setShowNamePrompt(false);
      setMessages(prev => [...prev, { role: 'assistant', text: `धन्यवाद ${visitorName} जी! आपकी जानकारी हमारे पास आ गई है। Guruji जल्द ही आपसे संपर्क करेंगे।` }]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // Feb 08, 2026 Mobile-UX cleanup: WhatsApp option removed from floating menu
  // to prevent duplication with the Sticky Mobile CTA. Only AI Guruji Chat +
  // Call Guruji remain in the speed-dial.

  const handleCall = () => {
    setMenuOpen(false);
    trackConsultationCTAClick('floating_call');
    window.location.href = `tel:${CALL_PHONE}`;
  };

  // Suppress on pages where it would block content (see HIDE_ON above)
  if (hide) return null;

  return (
    <>
      {/* Speed-dial menu options (appear above the launcher when open).
          Feb 08, 2026 Mobile-UX: only AI Guruji Chat + Call Guruji here;
          WhatsApp intentionally lives ONLY in the Sticky Mobile CTA to avoid
          duplication. bottom-44 keeps clear separation above the shrunk
          Spiritual Robot launcher (bottom: 130px on mobile). */}
      {menuOpen && !chatOpen && (
        <div className="fixed bottom-[220px] right-5 z-[60] flex flex-col items-end gap-3 animate-fade-in-up" data-testid="floating-actions-menu">
          <button
            onClick={openChat}
            className="flex items-center gap-2 pl-3 pr-4 py-2.5 bg-gradient-to-r from-yellow-600 to-amber-500 text-purple-950 rounded-full shadow-xl font-semibold text-sm hover:scale-105 active:scale-95 transition-transform"
            data-testid="fa-chat-btn"
          >
            <MessageCircle className="w-4 h-4" />
            AI Guruji Chat
          </button>
          <button
            onClick={handleCall}
            className="flex items-center gap-2 pl-3 pr-4 py-2.5 bg-purple-700 text-white rounded-full shadow-xl font-semibold text-sm hover:scale-105 active:scale-95 transition-transform"
            data-testid="fa-call-btn"
          >
            <Phone className="w-4 h-4" />
            Call Guruji
          </button>
        </div>
      )}

      {/* Single launcher (spiritual robot) */}
      {!chatOpen && (
        <SpiritualRobot
          data-testid="fa-launcher"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
        />
      )}

      {/* Backdrop to close menu when tapping outside */}
      {menuOpen && !chatOpen && (
        <div className="fixed inset-0 z-[50]" onClick={() => setMenuOpen(false)} data-testid="fa-backdrop" />
      )}

      {/* Chat window */}
      {chatOpen && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-16 right-4 z-[9999] w-[360px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-5rem)] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-yellow-800/40"
          style={{ background: 'linear-gradient(180deg, #1a0710 0%, #0f0508 100%)' }}
        >
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-yellow-800/40 to-purple-900/40 border-b border-yellow-900/30">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-700 flex items-center justify-center text-white text-sm font-bold">VM</div>
            <div className="flex-1">
              <p className="text-yellow-300 text-sm font-semibold">ViratAstro Assistant</p>
              <p className="text-green-400 text-xs flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" /> Online
              </p>
            </div>
            <button onClick={() => setChatOpen(false)} data-testid="chatbot-close-btn"
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1 scrollbar-thin">
            {messages.map((msg, i) => <ChatMessage key={i} msg={msg} />)}
            {loading && <TypingDots />}
            {showNamePrompt && !nameSubmitted && (
              <div className="bg-[#3d101e] border border-yellow-800/30 rounded-xl p-3 mt-2" data-testid="chatbot-name-prompt">
                <p className="text-yellow-300 text-xs mb-2">अगर आप चाहें तो अपना नाम और नंबर दें, Guruji personally आपसे बात करेंगे:</p>
                <input type="text" placeholder="आपका नाम" value={visitorName}
                  onChange={e => setVisitorName(e.target.value)} data-testid="chatbot-name-input"
                  className="w-full mb-2 px-3 py-1.5 rounded-lg bg-[#0f0508] border border-yellow-900/30 text-white text-sm placeholder-gray-500 focus:border-yellow-600 outline-none" />
                <input type="tel" placeholder="Phone / WhatsApp नंबर" value={visitorPhone}
                  onChange={e => setVisitorPhone(e.target.value)} data-testid="chatbot-phone-input"
                  className="w-full mb-2 px-3 py-1.5 rounded-lg bg-[#0f0508] border border-yellow-900/30 text-white text-sm placeholder-gray-500 focus:border-yellow-600 outline-none" />
                <div className="flex gap-2">
                  <button onClick={handleNameSubmit} data-testid="chatbot-name-submit"
                    className="flex-1 py-1.5 bg-gradient-to-r from-yellow-600 to-yellow-700 text-white text-xs rounded-lg font-medium hover:from-yellow-500 hover:to-yellow-600 transition-all">
                    Submit
                  </button>
                  <button onClick={() => setShowNamePrompt(false)} className="px-3 py-1.5 text-gray-400 text-xs hover:text-white transition-colors">बाद में</button>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="px-3 py-2.5 border-t border-yellow-900/30 bg-[#0f0508]/80">
            <div className="flex items-center gap-2">
              <input ref={inputRef} type="text" value={input}
                onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                placeholder="अपना सवाल पूछें..." disabled={loading} data-testid="chatbot-input"
                className="flex-1 px-4 py-2.5 rounded-full bg-[#3d101e] border border-yellow-900/30 text-white text-sm placeholder-gray-500 focus:border-yellow-600 outline-none disabled:opacity-50" />
              <button onClick={sendMessage} disabled={!input.trim() || loading} data-testid="chatbot-send-btn"
                className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-600 to-yellow-700 text-white flex items-center justify-center hover:from-yellow-500 hover:to-yellow-600 transition-all disabled:opacity-40">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
